import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { LuPlus } from "react-icons/lu";
import { PageLayout } from "../../app/PageLayout";
import { Button, EmptyState, QtyStepper, SegmentedControl, Skeleton } from "../../components/ui";
import { CatalogList } from "../../components/patterns/CatalogList";
import { KitList } from "../../components/patterns/KitList";
import { KitBuilder } from "../kits";
import { useOrderDraft } from "../../context/OrderDraftContext";
import { plural, t } from "../../i18n";
import { ptBR } from "../../i18n/pt-BR";
import { useFreeChoice } from "./useFreeChoice";
import { CUSTOM, STANDARD, useProcedureKits } from "./useProcedureKits";
import { PATHS } from "../../app/paths";

const FREE = "escolha-livre";
const KITS = "pacotes";

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h2 className="text-body font-semibold text-fg-secondary">{children}</h2>
);

export const ProceduresPage: React.FC = () => {
  const navigate = useNavigate();
  const { selectedButtonCheckKits: mode, setSelectedButtonCheckKits, setSelectedOpmeKitIndex, setSelectedProcedureKitIndex, setModificadoProcedimentos } = useOrderDraft();
  const isFree = mode === FREE;
  const free = useFreeChoice();
  const kits = useProcedureKits(!isFree);
  const [creating, setCreating] = useState(false);

  const goBack = () => {
    setSelectedOpmeKitIndex(-1);
    setSelectedProcedureKitIndex(-1);
    setModificadoProcedimentos(false);
    navigate(PATHS.newOrder);
  };

  const count = free.selected.length;
  const cta = isFree ? (
    <Button variant="primary" size="lg" loading={free.saving} disabled={!count} onClick={free.confirm}>
      {count ? plural(count, ptBR.procedures.confirmCount) : t("procedures.confirm")}
    </Button>
  ) : (
    <Button variant="primary" size="lg" loading={kits.saving} disabled={!kits.selectedKit} onClick={kits.confirm}>
      {kits.selectedKit ? t("procedures.confirmKit") : t("procedures.confirm")}
    </Button>
  );
  const ctaHint = isFree ? (count ? undefined : t("procedures.freeMissing")) : kits.selectedKit?.name ?? t("procedures.kitMissing");

  return (
    <PageLayout title={t("procedures.title")} onBack={goBack} nav={false} cta={cta} ctaHint={ctaHint}>
      <SegmentedControl
        aria-label={t("procedures.mode")}
        className="w-full max-w-sm self-center"
        value={isFree ? FREE : KITS}
        onChange={setSelectedButtonCheckKits}
        options={[
          // Só aparece depois de carregar: a busca dos pacotes só roda com a aba aberta.
          { value: KITS, label: t("procedures.kits"), count: kits.kits?.length || undefined },
          { value: FREE, label: t("procedures.free"), count: count || undefined },
        ]}
      />
      <p className="-mt-1.5 text-center text-caption text-fg-muted">{t(isFree ? "procedures.freeHint" : "procedures.kitsHint")}</p>

      {isFree ? (
        <>
          <SectionTitle>{t("procedures.identified")}</SectionTitle>
          {count ? (
            <ul className="stagger flex flex-col gap-2.5">
              {free.selected.map((p) => (
                <li key={p.code} className="flex items-center gap-3 rounded-control border border-line bg-surface py-3 pl-4 pr-3 shadow-card">
                  <p className="min-w-0 flex-1 break-words text-label">
                    <span className="block font-mono text-caption font-semibold text-primary">{p.code}</span>
                    {p.description}
                  </p>
                  <QtyStepper value={p.quantity} onChange={(q) => free.setQuantity(p.code, q)} onRemove={() => free.remove(p.code)} label={p.description} />
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState title={t("procedures.emptyTitle")} description={t("procedures.emptyHint")} />
          )}

          <SectionTitle>{t("procedures.addOthers")}</SectionTitle>
          <CatalogList
            items={free.catalog?.map((p) => ({ id: p.cod_procedimento, code: p.cod_procedimento, label: p.nome })) ?? null}
            addedIds={new Set(free.selected.map((p) => p.code))}
            onAdd={(entry) => free.add(entry.id, entry.label)}
            searchLabel={t("procedures.search")}
            placeholder={t("procedures.searchPlaceholder")}
            empty={() => t("procedures.notFound")}
          />
        </>
      ) : (
        <>
          <SectionTitle>{t("procedures.chooseKit")}</SectionTitle>
          <SegmentedControl
            aria-label={t("procedures.kitType")}
            tone="soft"
            value={kits.type}
            onChange={kits.changeType}
            options={[
              { value: STANDARD, label: t("procedures.standard") },
              { value: CUSTOM, label: t("procedures.custom") },
            ]}
          />
          <p className="rounded-control border-l-4 border-primary bg-primary-tint px-4 py-3 text-label text-fg-secondary">
            <strong className="text-fg">{t(kits.custom ? "procedures.customTitle" : "procedures.standardTitle")}</strong>{" "}
            {t(kits.custom ? "procedures.customText" : "procedures.standardText")} {t("procedures.aiText")}
          </p>

          {kits.kits === null ? (
            [0, 1].map((i) => <Skeleton key={i} className="h-32 rounded-card" />)
          ) : kits.kits.length ? (
            <KitList kits={kits.kits} selected={kits.selected} onSelect={kits.select} onRecommend={kits.recommend} recommending={kits.recommending} itemLabel={(p) => `${p.code} - ${p.description}`} />
          ) : (
            <EmptyState
              title={t(kits.custom ? "procedures.noCustom" : "procedures.noStandard")}
              description={t(kits.custom ? "procedures.noCustomHint" : "procedures.noStandardHint")}
            />
          )}

          <Button variant="text" className="self-start" onClick={() => setCreating(true)}>
            <LuPlus size={18} aria-hidden />
            {t("procedures.newKit")}
          </Button>
          {creating && (
            <KitBuilder
              kind="proc"
              onClose={() => setCreating(false)}
              onCreated={(kind, name) => {
                setCreating(false);
                // Pacote de OPME criado daqui aparece na tela de OPME.
                if (kind === "proc") kits.showCreated(name);
              }}
            />
          )}
        </>
      )}
    </PageLayout>
  );
};
